"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
/**
 * Sistema de Classificação do Estado Nutricional
 * Baseado nos dados da OMS com adaptações
 * Classificação por faixas etárias: Crianças, Adolescentes, Adultos e Idosos
 */

var PI_MINIMO = Number.MIN_SAFE_INTEGER;
var PI_MAXIMO = Number.MAX_SAFE_INTEGER;
var ZI_MINIMO = Number.MIN_SAFE_INTEGER;
var ZI_MAXIMO = Number.MAX_SAFE_INTEGER;

/**
 * Função principal que classifica o estado nutricional
 * @param {number} idade - Idade em meses
 * @param {number} alturaCm - Altura em centímetros
 * @param {number} pesoKg - Peso em quilogramas
 * @param {array} planilha - Planilha com os dados da OMS para comparação
 * @returns {Object} - Resultado da classificação nutricional
 */
function classificacaoNutricional(idade, alturaCm, pesoKg) {
  var planilha = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  alturaCm = Math.floor(alturaCm);

  // Cálculo do IMC
  var alturaMetros = alturaCm / 100;
  var imc = pesoKg / (alturaMetros * alturaMetros);

  // Determinar grupo etário e aplicar classificação apropriada
  var resultado;

  // 1. Crianças (< 10 anos ou 120 meses)
  if (idade <= 120) {
    resultado = classificarCrianca(idade, pesoKg, alturaCm, imc, planilha);
  }
  // 2. Adolescentes (≥ 10 anos e < 19 anos ou 120-228 meses)
  else if (idade > 120 && idade < 229) {
    resultado = classificarAdolescente(idade, alturaCm, imc, planilha);
  }
  // 3. Adultos (≥ 19 anos e < 60 anos ou 299-719 meses)
  else if (idade >= 229 && idade < 720) {
    resultado = classificarAdulto(imc);
  }
  // 4. Idosos (≥ 60 anos ou 720+ meses)
  else {
    resultado = classificarIdoso(imc);
  }
  return {
    sucesso: true,
    imc: imc.toFixed(2),
    resultados: resultado
  };
}

/**
 * Classificação para crianças (< 10 anos)
 * Cálculos: Peso/idade, Estatura/idade, Peso/estatura (0-5 anos), IMC/idade
 */
function classificarCrianca(idade, pesoKg, alturaCm, imc, planilha) {
  // Inicializar resultado
  var resultado = {
    grupoEtario: "Criança",
    indicadores: {}
  };

  // Todos os grupos de idade (0-10 anos) têm Peso/idade e Estatura/idade
  resultado.indicadores.pesoIdade = calcularIndicador('peso-idade', planilha, idade, pesoKg);
  resultado.indicadores.estaturaIdade = calcularIndicador('estatura-idade', planilha, idade, alturaCm);

  // Para crianças de 0-5 anos (60 meses), calcular Peso/estatura
  if (idade < 60) {
    resultado.indicadores.pesoEstatura = calcularIndicador('peso-estatura', planilha, alturaCm, pesoKg);
    resultado.indicadores.imc = calcularIndicador('imc-idade-0-5', planilha, idade, imc);
  }
  // Para crianças de 5-10 anos, calcular apenas IMC/idade
  else {
    resultado.indicadores.imc = calcularIndicador('imc-idade-5-10', planilha, idade, imc);
  }
  return resultado;
}

/**
 * Classificação para adolescentes (≥ 10 anos e < 20 anos)
 * Cálculos: Estatura/idade e IMC/idade
 */
function classificarAdolescente(idade, alturaCm, imc, planilha) {
  var resultado = {
    grupoEtario: "Adolescente",
    indicadores: {}
  };

  // Calcular Estatura/idade e IMC/idade
  resultado.indicadores.estaturaIdade = calcularIndicador('estatura-idade', planilha, idade, alturaCm);
  resultado.indicadores.imc = calcularIndicador('imc-idade-10-20', planilha, idade, imc);
  return resultado;
}

/**
 * Classificação para adultos (≥ 20 anos e < 60 anos)
 * Cálculo: IMC
 */
function classificarAdulto(imc) {
  var classificacao = "";
  var intervalo = "";
  if (imc < 18.5) {
    classificacao = "Baixo peso";
    intervalo = "< 18.5";
  } else if (imc >= 18.5 && imc < 25) {
    classificacao = "Adequado ou eutrófico";
    intervalo = "18.5 – 24.9";
  } else if (imc >= 25 && imc < 30) {
    classificacao = "Sobrepeso";
    intervalo = "25 – 29.9";
  } else if (imc >= 30 && imc < 35) {
    classificacao = "Obesidade grau I";
    intervalo = "30 – 34.9";
  } else if (imc >= 35 && imc < 40) {
    classificacao = "Obesidade grau II";
    intervalo = "35 – 39.9";
  } else {
    classificacao = "Obesidade grau III";
    intervalo = "≥ 40";
  }
  return {
    grupoEtario: "Adulto",
    indicadores: {
      imc: {
        valor: imc.toFixed(2),
        classificacao: classificacao,
        intervaloReferencial: intervalo
      }
    }
  };
}

/**
 * Classificação para idosos (≥ 60 anos)
 * Cálculo: IMC com pontos de corte específicos
 */
function classificarIdoso(imc) {
  var classificacao = "";
  var intervalo = "";

  // Pontos de corte diferentes para idosos
  if (imc < 22) {
    classificacao = "Baixo peso";
    intervalo = "< 22";
  } else if (imc >= 22 && imc < 27) {
    classificacao = "Adequado ou eutrófico";
    intervalo = "22 – 26.9";
  } else if (imc >= 27) {
    classificacao = "Sobrepeso";
    intervalo = "≥ 27";
  }
  return {
    grupoEtario: "Idoso",
    indicadores: {
      imc: {
        valor: imc.toFixed(2),
        classificacao: classificacao,
        intervaloReferencial: intervalo
      }
    }
  };
}

/**
 * Calcula indicadores (escore-z e percentil) baseado em dados de referência
 */
function calcularIndicador(tipoIndicador, planilha, valor1, valor2) {
  var dadoEncontrado = buscarPlanilha(tipoIndicador, planilha, valor1, valor2);
  if (!dadoEncontrado) return null;
  var escoreZ = dadoEncontrado.escoreZ,
    percentil = dadoEncontrado.percentil,
    intervaloPercentil = dadoEncontrado.intervaloPercentil,
    intervaloEscoreZ = dadoEncontrado.intervaloEscoreZ;

  // Classificação baseada no escore-z e percentil
  var classificacaoEscoreZ = classificarPorEscoreZ(tipoIndicador, escoreZ);
  var classificacaoPercentil = classificarPorPercentil(tipoIndicador, percentil);
  return {
    valorPercentil: percentil,
    valorEscoreZ: escoreZ,
    intervaloPercentil: intervaloPercentil,
    intervaloEscoreZ: intervaloEscoreZ,
    classificacaoEscoreZ: classificacaoEscoreZ,
    classificacaoPercentil: classificacaoPercentil
  };
}
function buscarPlanilha(tipoIndicador, planilha, valor1, valor2) {
  switch (tipoIndicador) {
    case 'peso-idade':
      return buscarDados(valor1, valor2, planilha[0]);
    case 'estatura-idade':
      return buscarDados(valor1, valor2, planilha[1]);
    case 'peso-estatura':
      return buscarDados(valor1, valor2, planilha[2]);
    case 'imc-idade-0-5':
    case 'imc-idade-5-10':
    case 'imc-idade-10-20':
      return buscarDados(valor1, valor2, planilha[3]);
    default:
      return null;
  }
}
function buscarDados(valor1, valor2, subplanilha) {
  var linha, cabecalho;
  subplanilha.eachRow(function (row, rowNumber) {
    if (rowNumber === 1) {
      cabecalho = row.values;
    } else {
      var linhaAtual = row.values;
      if (linhaAtual[1] === valor1) {
        linha = linhaAtual;
      }
    }
  });
  if (!linha) return null;
  return encontrarIntervalos(linha, cabecalho, valor2);
}

/**
 * 
 * @param {*} linha linha de tabela a ser verificada
 * @param {*} cabecalho cabeçalho da tabela com os valores "Pi" e "Zi"
 * @param {*} valor1 valor a ser encontrado nos intervalos disponíveis
 * @returns 
 */
function encontrarIntervalos(linha, cabecalho, valor) {
  // Encontrar o intervalo para percentis (p...)
  var intervaloPi = null;
  var labelPi = null;

  // Normaliza os dados para substituir todas as vírgulas por pontos.
  valor = Number((valor + '').replace(',', '.'));
  linha = linha.map(function (l) {
    return Number((l + '').replace(',', '.'));
  });

  // Verificar se está abaixo do primeiro percentil
  if (valor < linha[2]) {
    intervaloPi = PI_MINIMO;
    labelPi = "< ".concat(cabecalho[2]);
  } else if (valor >= linha[10]) {
    // Verificar se está acima do último percentil
    intervaloPi = PI_MAXIMO;
    labelPi = "\u2265 ".concat(cabecalho[10]);
  } else {
    // Verificar em qual intervalo está
    for (var i = 2; i < 10; i++) {
      if (valor >= linha[i] && valor < linha[i + 1]) {
        intervaloPi = (Number(cabecalho[i].replace('p', '').replace(',', '.')) + Number(cabecalho[i + 1].replace('p', '').replace(',', '.'))) / 2.0;
        labelPi = "".concat(cabecalho[i], " \u2013 ").concat(cabecalho[i + 1]);
        break;
      }
    }
  }

  // Encontrar o intervalo para z-scores (z...)
  var intervaloZi = null;
  var labelZi = null;

  // Verificar se está abaixo do primeiro z-score
  if (valor < linha[11]) {
    intervaloZi = ZI_MINIMO;
    labelZi = "< ".concat(cabecalho[11]);
  } else if (valor >= linha[17]) {
    // Verificar se está acima do último z-score
    intervaloZi = ZI_MAXIMO;
    labelZi = "\u2265 ".concat(cabecalho[17]);
  } else {
    // Verificar em qual intervalo está
    for (var _i = 11; _i < 17; _i++) {
      if (valor >= linha[_i] && valor < linha[_i + 1]) {
        intervaloZi = (Number(cabecalho[_i].replace('z', '')) + Number(cabecalho[_i + 1].replace('z', ''))) / 2.0;
        labelZi = "".concat(cabecalho[_i], " \u2013 ").concat(cabecalho[_i + 1]);
        break;
      }
    }
  }
  return {
    percentil: intervaloPi,
    escoreZ: intervaloZi,
    intervaloPercentil: labelPi,
    intervaloEscoreZ: labelZi
  };
}

/**
 * Classifica o estado nutricional baseado no escore-z do indicador
 */
function classificarPorEscoreZ(tipoIndicador, escoreZ) {
  // Diferentes critérios de classificação por tipo de indicador

  // Classificação para Peso/Idade
  if (tipoIndicador === 'peso-idade') {
    if (escoreZ < -3) return "Muito baixo peso para a idade";
    if (escoreZ < -2) return "Baixo peso para a idade";
    if (escoreZ <= 2) return "Peso adequado para a idade";
    return "Peso elevado para a idade";
  }

  // Classificação para Estatura/Idade
  if (tipoIndicador === 'estatura-idade') {
    if (escoreZ < -3) return "Muito baixa estatura para a idade";
    if (escoreZ < -2) return "Baixa estatura para a idade";
    return "Estatura adequada para a idade";
  }

  // Classificação para Peso/Estatura
  if (tipoIndicador === 'peso-estatura') {
    if (escoreZ < -3) return "Magreza acentuada";
    if (escoreZ < -2) return "Magreza";
    if (escoreZ <= 1) return "Eutrofia";
    if (escoreZ <= 2) return "Risco de sobrepeso";
    if (escoreZ <= 3) return "Sobrepeso";
    return "Obesidade";
  }

  // Classificação para IMC/Idade (0-5 anos)
  if (tipoIndicador === 'imc-idade-0-5') {
    if (escoreZ < -3) return "Magreza acentuada";
    if (escoreZ < -2) return "Magreza";
    if (escoreZ <= 1) return "Eutrofia";
    if (escoreZ <= 2) return "Risco de sobrepeso";
    if (escoreZ <= 3) return "Sobrepeso";
    return "Obesidade";
  }

  // Classificação para IMC/Idade (5-10 anos e 10-20 anos)
  if (tipoIndicador === 'imc-idade-5-10' || tipoIndicador === 'imc-idade-10-20') {
    if (escoreZ < -3) return "Magreza acentuada";
    if (escoreZ < -2) return "Magreza";
    if (escoreZ <= 1) return "Eutrofia";
    if (escoreZ <= 2) return "Sobrepeso";
    if (escoreZ <= 3) return "Obesidade";
    return "Obesidade grave";
  }
  return "Classificação indeterminada";
}

/**
 * Classifica o estado nutricional baseado no percentil do indicador
 */
function classificarPorPercentil(tipoIndicador, percentil) {
  // Classificação para Peso/Idade
  if (tipoIndicador === 'peso-idade') {
    if (percentil < 0.1) return "Muito baixo peso para a idade";
    if (percentil < 3) return "Baixo peso para a idade";
    if (percentil <= 97) return "Peso adequado para a idade";
    return "Peso elevado para a idade";
  }

  // Classificação para Estatura/Idade
  if (tipoIndicador === 'estatura-idade') {
    if (percentil < 0.1) return "Muito baixa estatura para a idade";
    if (percentil < 3) return "Baixa estatura para a idade";
    return "Estatura adequada para a idade";
  }

  // Classificação para Peso/Estatura
  if (tipoIndicador === 'peso-estatura') {
    if (percentil < 0.1) return "Magreza acentuada";
    if (percentil < 3) return "Magreza";
    if (percentil <= 85) return "Eutrofia";
    if (percentil <= 97) return "Risco de sobrepeso";
    if (percentil <= 99.9) return "Sobrepeso";
    return "Obesidade";
  }

  // Classificação para IMC/Idade (0-5 anos)
  if (tipoIndicador === 'imc-idade-0-5') {
    if (percentil < 0.1) return "Magreza acentuada";
    if (percentil < 3) return "Magreza";
    if (percentil <= 85) return "Eutrofia";
    if (percentil <= 97) return "Risco de sobrepeso";
    if (percentil <= 99.9) return "Sobrepeso";
    return "Obesidade";
  }

  // Classificação para IMC/Idade (5-10 anos)
  if (tipoIndicador === 'imc-idade-5-10') {
    if (percentil < 0.1) return "Magreza acentuada";
    if (percentil < 3) return "Magreza";
    if (percentil <= 85) return "Eutrofia";
    if (percentil <= 97) return "Sobrepeso";
    if (percentil <= 99.9) return "Obesidade";
    return "Obesidade grave";
  }

  // Classificação para IMC/Idade (10-20 anos)
  if (tipoIndicador === 'imc-idade-10-20') {
    if (percentil < 0.1) return "Magreza acentuada";
    if (percentil < 3) return "Magreza";
    if (percentil <= 85) return "Eutrofia";
    if (percentil <= 97) return "Sobrepeso";
    if (percentil <= 99.9) return "Obesidade";
    return "Obesidade grave";
  }
  return "Classificação indeterminada";
}
var _default = exports["default"] = classificacaoNutricional;